import { makeBrandedId } from "@captions-web/utils/brand";
import { createUseStoreWithSelectorDeferred } from "@captions-web/utils/zustand-store";
import { MutableRefObject } from "react";
import { createStore } from "zustand";
import { combine } from "zustand/middleware";
import { compute, computed } from "zustand-computed-state";

import { loadVideoToFile } from "~/lib/load-video-to-file";
import { concatenateMP4 } from "~/modules/video/concat-mp4";
import { createStoreGetters } from "~/stores/root";

import { VideoDetailsClipItemVariantType } from "../network/schemas";

type ClipFileState = {
  clipId: string;
  state: "CLIP_NOT_READY" | "FETCHING_TO_FILE" | "READY" | "ERROR";
  videoUrl: string | null;
  file: File | null;
};

const ClipSequenceId = makeBrandedId("clip_seq");
export type ClipSequenceId = ReturnType<typeof ClipSequenceId>;

type CompositedFileState =
  | {
      state: "LOADING" | "ERROR";
      file: File | null;
      clipIds: string[];
      url: string | null;
      abortController: AbortController | null;
      sequenceId: ClipSequenceId;
      videoPlayerRef: MutableRefObject<HTMLVideoElement>;
    }
  | {
      state: "COMPLETE";
      file: File;
      clipIds: string[];
      url: string;
      abortController: AbortController | null;
      sequenceId: ClipSequenceId;
      videoPlayerRef: MutableRefObject<HTMLVideoElement>;
    };

type ClipSequenceSelectorState = {
  compositedFilePool: Map<ClipSequenceId, CompositedFileState>;
  clipFilePool: Map<string, ClipFileState>;
  currentClipSequence: string[];
  currentCompositedFile: CompositedFileState | null;
  isReadyToComposite: boolean;
};

const initialState: ClipSequenceSelectorState = {
  currentClipSequence: [] as string[],
  clipFilePool: new Map(),
  compositedFilePool: new Map(),
  currentCompositedFile: null,
  isReadyToComposite: false,
};

export function createClipSequenceSelectorStore() {
  return createStore(
    computed(
      combine(initialState, (set, get) => {
        const updateClip = (clipId: string, update: Partial<ClipFileState>) => {
          const inputFiles = new Map(get().clipFilePool);
          const currentFile = inputFiles.get(clipId);
          const defaultValues = {
            state: "CLIP_NOT_READY" as const,
            videoUrl: null,
            file: null,
          };

          inputFiles.set(clipId, {
            ...defaultValues,
            ...(currentFile || {}),
            ...update,
            clipId,
          });

          set({ clipFilePool: inputFiles });
        };

        const updateCompositedFile = (clipIds: string[], update: Partial<CompositedFileState>) => {
          const compositedFilePool = new Map(get().compositedFilePool);
          const sequenceId = ClipSequenceId(clipIds.join(","));
          const currentFile = compositedFilePool.get(sequenceId);
          const defaultValues = {
            state: "LOADING" as const,
            file: null,
            url: null,
            abortController: null,
            videoPlayerRef: { current: document.createElement("video") },
          };

          try {
            const nextState = {
              ...defaultValues,
              ...(currentFile || {}),
              ...update,
              clipIds,
              sequenceId,
            };
            if (nextState.state === "COMPLETE" && (!nextState.file || !nextState.url)) {
              throw new Error("Invalid COMPLETE state: file and url must be defined");
            }

            compositedFilePool.set(sequenceId, nextState as CompositedFileState);
          } catch (error) {
            compositedFilePool.set(sequenceId, {
              ...(currentFile || {}),
              state: "ERROR",
              clipIds,
            } as CompositedFileState);
          }
          set({ compositedFilePool });
        };

        const compositeClipsToFile = async (clipIds: string[]) => {
          if (!clipIds.length) {
            return;
          }

          const sequenceId = ClipSequenceId(clipIds.join(","));
          const previousFile = get().compositedFilePool.get(sequenceId);

          // abort and start over if already loading
          if (previousFile?.state === "LOADING") {
            previousFile.abortController?.abort();
          }
          // skip re-rendering if already complete
          if (previousFile?.state === "COMPLETE") {
            return;
          }

          const { isReadyToComposite } = get();
          if (!isReadyToComposite) {
            return;
          }

          try {
            const { clipFilePool } = get();
            const videoFiles = clipIds.map((id) => {
              const file = clipFilePool.get(id);
              if (!file || file.state !== "READY" || !file.file) {
                throw new Error(`Clip ${id} not found or not ready`);
              }
              return file.file;
            });
            updateCompositedFile(clipIds, { state: "LOADING" });
            const outputFile = await concatenateMP4(videoFiles);
            const blobUrl = URL.createObjectURL(outputFile);

            updateCompositedFile(clipIds, {
              state: "COMPLETE",
              file: outputFile,
              url: blobUrl,
            });
          } catch (error) {
            console.error(error);
            updateCompositedFile(clipIds, { state: "ERROR" });
          }
        };

        const loadClipToPool = async (clip: VideoDetailsClipItemVariantType) => {
          const { clipId } = clip;
          const previousClip = get().clipFilePool.get(clipId);
          // clips are immutable, if we've loaded the clip id already no need to reload
          if (previousClip && previousClip.state === "READY") {
            return;
          }

          // if backend is still processing, add it to the pool with no file attached yet
          if (clip.state === "PROCESSING" || clip.state === "QUEUED") {
            updateClip(clipId, { state: "CLIP_NOT_READY" });
            return;
          }

          if (clip.state === "FAILED") {
            updateClip(clipId, { state: "ERROR" });
            return;
          }
          try {
            if (clip.state !== "COMPLETE") {
              throw new Error(`State changed to ${clip.state} but no videoUrl available`);
            }
            // load the clip
            updateClip(clipId, { videoUrl: clip.videoUrl, state: "FETCHING_TO_FILE" });
            const file = await loadVideoToFile(clip.videoUrl);
            updateClip(clipId, { file, state: "READY" });
          } catch (error) {
            console.error("failed to load clip to pool", error);
            updateClip(clipId, { state: "ERROR" });
          }
        };

        const loadClipsToPool = async (clips: VideoDetailsClipItemVariantType[]) => {
          await Promise.all(clips.map((clip) => loadClipToPool(clip)));
        };

        const setCurrentClipSequence = (sequence: string[]) => {
          set({
            currentClipSequence: sequence,
          });
        };

        const reset = () => {
          const compositedFilePool = get().compositedFilePool;
          for (const [_, file] of compositedFilePool) {
            if (file.url) {
              URL.revokeObjectURL(file.url);
            }
            if (file.abortController) {
              file.abortController.abort();
            }
          }
          set(initialState);
        };

        return {
          async init() {},
          reset,
          loadClipToPool,
          compositeClipsToFile,
          loadClipsToPool,
          setCurrentClipSequence,
          ...compute(get, (state) => {
            const { currentClipSequence, compositedFilePool, clipFilePool } = state;
            const currentCompositedFile = compositedFilePool.get(
              ClipSequenceId(currentClipSequence.join(","))
            );
            const relevantClipFiles = currentClipSequence.map((item) => clipFilePool.get(item));
            const isReadyToComposite =
              relevantClipFiles.length > 0 &&
              relevantClipFiles.length === currentClipSequence.length &&
              relevantClipFiles.every((item) => item?.state === "READY");
            return {
              currentCompositedFile,
              isReadyToComposite,
            };
          }),
        };
      })
    )
  );
}

type ClipSequenceSelectorStore = ReturnType<
  ReturnType<typeof createClipSequenceSelectorStore>["getState"]
>;

export const {
  getStore: getClipSequenceSelectorStore,
  getStoreAsync: getClipSequenceSelectorStoreAsync,
  getStoreApi: getClipSequenceSelectorStoreApi,
} = createStoreGetters<ClipSequenceSelectorStore>("clip-sequence");

export const useClipSequenceSelectorStore = createUseStoreWithSelectorDeferred(() =>
  getClipSequenceSelectorStoreApi()
);
